/**
 * Service pour gérer les appels API liés aux widgets
 */

import { ApiService } from "./ApiService";
import {
  WidgetConfiguration,
  WidgetLanguage,
  FilterId,
  FILTER_DEFINITIONS,
  WidgetTemplateType,
  WidgetSortOption,
  DisplayedFieldPreviewId,
  DisplayedFieldId,
} from "../types/widgets.d";
import { WidgetBackend } from "../types/widgetBackend.d";
import { WidgetServiceConfig } from "../types/service.d";
import {
  ApidaeSearchParams,
  ObjetTouristique,
  FacetValue,
  ApidaeSearchResponse,
  ApidaeAgendaResponse,
  FacetId,
} from "../types/apidae.d";

export class WidgetService {
  private apiService: ApiService;

  constructor(config: WidgetServiceConfig) {
    this.apiService = new ApiService({
      baseURL: config.apiUrl,
      timeout: config.timeout || 10000,
      toastHandler: config.toastHandler,
      language: config.language,
    });
  }

  /**
   * Mappe un DisplayedFieldPreviewId vers le nom de champ de l'API Apidae
   */
  private mapPreviewFieldToApiField(fieldId: string): string[] {
    switch (fieldId) {
      case DisplayedFieldPreviewId.COMMUNE:
        return ["localisation"];
      case DisplayedFieldPreviewId.OUVERTURE:
        return ["ouverture"];
      case DisplayedFieldPreviewId.TELEPHONE:
        return ["informations"];
      case DisplayedFieldPreviewId.RESERVATION:
        return ["reservation"];
      case DisplayedFieldPreviewId.CATEGORIE:
        // La catégorie dépend du type d'objet, mais nécessite généralement les informations spécifiques
        return [
          "informationsActivite",
          "informationsCommerceEtService",
          "informationsDomaineSkiable",
          "informationsFeteEtManifestation",
          "informationsEquipement",
          "informationsHebergementCollectif",
          "informationsHebergementLocatif",
          "informationsHotelleriePleinAir",
          "informationsHotellerie",
          "informationsPatrimoineCulturel",
          "informationsPatrimoineNaturel",
          "informationsDegustation",
          "informationsRestauration",
          "informationsSejourPackage",
        ];
      default:
        return [];
    }
  }

  /**
   * Mappe un DisplayedFieldId vers le nom de champ de l'API Apidae
   */
  private mapDisplayedFieldToApiField(fieldId: string): string[] {
    switch (fieldId) {
      case DisplayedFieldId.DESCRIPTIF_COURT:
      case DisplayedFieldId.DESCRIPTIF_DETAILLE:
      case DisplayedFieldId.DESCRIPTIFS_THEMATISES:
        return ["presentation"];
      case DisplayedFieldId.IMAGES:
        return ["illustrations"];
      case DisplayedFieldId.MULTIMEDIAS:
        return ["multimedias"];
      case DisplayedFieldId.MOYENS_COMMUNICATION:
        return ["informations", "reservation"];
      case DisplayedFieldId.INFORMATIONS_RESERVATION:
        return ["reservation"];
      case DisplayedFieldId.OUVERTURE:
        return ["ouverture"];
      case DisplayedFieldId.TARIFS:
      case DisplayedFieldId.MOYEN_PAIEMENT:
        return ["descriptionTarif"];
      case DisplayedFieldId.ANIMAUX_ACCEPTES:
      case DisplayedFieldId.PUBLIC_NIVEAU:
        return ["prestations"];
      case DisplayedFieldId.EQUIPEMENTS:
        return [
          "prestations",
          "informationsActivite",
          "informationsSejourPackage",
        ]; // prestations pour prestations.equipements, prestations.services, prestations.conforts, prestations.activites ; informationsActivite et informationsSejourPackage pour leurs .activitesCulturelles et .activitesSportives
      case DisplayedFieldId.LABEL_DISTINCTION:
        return ["distinctions"];
      case DisplayedFieldId.ACCESSIBILITE:
        return ["prestations", "distinctions"]; // prestations pour prestations.tourismesAdaptes, prestations.descriptifHandicapAuditif, prestations.descriptifHandicapMental, prestations.descriptifHandicapMoteur, prestations.descriptifHandicapVisuel ; distinctions pour affichage du badge "Tourisme & Handicap" si présence d'au moins 1 des 4 distinctions "Tourisme & Handicap"
      case DisplayedFieldId.INFOS_ITINERAIRES:
        return ["informationsEquipement"];
      case DisplayedFieldId.CAPACITES:
        return ["capacites"];
      case DisplayedFieldId.LOCATION_SALLE:
        return ["tourismeAffaires"];
      case DisplayedFieldId.DESCRIPTIFS_PRIVES:
        return ["donneesPrivees"];
      case DisplayedFieldId.OBJETS_LIES:
        return ["liens"];
      case DisplayedFieldId.PRESTATAIRE_ACTIVITE:
        return ["informationsActivite"];
      case DisplayedFieldId.DISPONIBILITES_ELLOHA:
      case DisplayedFieldId.DISPONIBILITES_OPENSYSTEM:
        // id produit / ui depuis metadonnees de l'objet
        return ["metadonnees"];
      default:
        return [];
    }
  }

  /**
   * Construit la liste des responseFields pour l'API Apidae
   */
  private buildResponseFields(config: WidgetConfiguration): string[] {
    // Champs de base toujours nécessaires
    const baseFields = [
      "id",
      "type",
      "nom",
      "localisation",
      "ouverture",
      "illustrations",
    ];

    // Champs pour displayedFieldsPreview (listing)
    const previewFields = new Set<string>();
    config.contentCustomization.displayedFieldsPreview?.forEach((field) => {
      this.mapPreviewFieldToApiField(field.id).forEach((apiField) => {
        previewFields.add(apiField);
      });
    });

    // Champs pour displayedFields (listing + détail : tout chargé d'un coup)
    const displayedFields = new Set<string>();
    config.contentCustomization.displayedFields?.forEach((field) => {
      this.mapDisplayedFieldToApiField(field.id).forEach((apiField) => {
        displayedFields.add(apiField);
      });
    });

    // Combiner tous les champs
    const allFields = new Set<string>([
      ...baseFields,
      ...previewFields,
      ...displayedFields,
    ]);
    return Array.from(allFields);
  }

  /**
   * Récupère la configuration d'un widget depuis l'API backend
   */
  async getWidgetConfig(widgetId: string): Promise<WidgetConfiguration> {
    try {
      const data: WidgetBackend = await this.apiService.get<WidgetBackend>(
        `/api/v002/public/widgets/${widgetId}`,
      );

      // Parser la config JSON stringifiée
      const config: WidgetConfiguration = JSON.parse(data.config);

      // Conserver le type du widget (utiliser data.type s'il existe, sinon garder celui de la config)
      if (data.type) {
        config.templateType = data.type;
      } else if (!config.templateType) {
        // Si ni data.type ni config.templateType n'existent, utiliser LIST par défaut
        config.templateType = WidgetTemplateType.LIST;
      }

      return config;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération de la configuration:",
        error,
      );
      throw new Error(
        `Impossible de charger la configuration du widget ${widgetId}`,
      );
    }
  }

  /**
   * Récupère les données touristiques d'un widget depuis l'API avec pagination.
   * Les responseFields incluent displayedFieldsPreview et displayedFields (listing + détail).
   * @param facetsConfig Surcharge du nombre d'éléments par facet (ex: { COMMUNE: 21, SOUS_TYPE: 31 }). Clés = FacetId.
   * @param facetsOnly Si true, demande 0 OT (first=0, count=0) pour ne récupérer que les facets (mise à jour "voir plus").
   */
  async getWidgetData(
    modePreview: boolean,
    widgetId: string,
    config: WidgetConfiguration,
    page: number = 1,
    pageSize: number = 10,
    dateDebut?: string,
    dateFin?: string,
    identifiants?: number[],
    center?: { type: "Point"; coordinates: [number, number] },
    radius?: number,
    filters?: Map<FilterId, Set<string>>,
    facetsConfig?: Record<string, number>,
    facetsOnly?: boolean,
  ): Promise<{
    items: ObjetTouristique[];
    facets: Map<FacetId, FacetValue[]>;
    total: number;
    hasMore: boolean;
    groupedByDate?: Record<string, ObjetTouristique[]>;
    numFoundParents?: number;
  }> {
    try {
      // Construire la query pour l'API
      const selectionIds = config.selections.map((s) => parseInt(s.id));

      // Inclure systématiquement le français dans les locales
      const locales = [config.contentCustomization.language];
      if (
        config.contentCustomization.language !== WidgetLanguage.FR &&
        !locales.includes(WidgetLanguage.FR)
      ) {
        locales.push(WidgetLanguage.FR);
      }

      // Construire les responseFields (displayedFieldsPreview + displayedFields) pour listing et détail
      const responseFields = this.buildResponseFields(config);

      const effectiveFirst = facetsOnly ? 0 : (page - 1) * pageSize;
      const effectiveCount = facetsOnly ? 0 : pageSize;

      const params: ApidaeSearchParams = {
        projetId: config.projetId,
        selectionIds: selectionIds,
        responseFields: responseFields,
        locales: locales,
        first: effectiveFirst,
        count: effectiveCount,
        dateDebut: dateDebut,
        dateFin: dateFin,
        identifiants: identifiants,
        identifiantsIgnores: config.contentCustomization.excludedOfferIds.map(
          (id) => parseInt(id),
        ),
        order:
          config.templateType === WidgetTemplateType.AGENDA ||
          config.templateType === WidgetTemplateType.AGENDA_MAP
            ? WidgetSortOption.DATE_OUVERTURE
            : WidgetSortOption.IDENTIFIANT,
      };

      let filtersToSend = new Map<FacetId, FacetValue[]>();
      filters?.forEach((values, key) => {
        if (FILTER_DEFINITIONS[key].facet) {
          filtersToSend.set(
            FILTER_DEFINITIONS[key].facet,
            Array.from(values).map((v) => ({ val: v })),
          );
        }
      });

      // Si center et radius sont fournis explicitement, les utiliser
      // Si communeCodesInsee est aussi fourni, l'utiliser en complément
      if (center && radius) {
        params.center = center;
        params.radius = radius;
      } else {
        // Sinon, utiliser les paramètres de filtrage géographique de la config si présents
        const geoPeri = config.contentCustomization.geographicPerimeter;
        if (geoPeri) {
          if (geoPeri.type === "communes") {
            let communes: FacetValue[] =
              filtersToSend.get(FacetId.COMMUNE) || [];
            geoPeri.communeIds?.forEach((commune) => {
              communes.push({
                val: commune,
                label: commune,
                count: 0,
              });
            });
            if (communes.length > 0) {
              filtersToSend.set(FacetId.COMMUNE, communes);
            }
          }
          if (geoPeri.type === "radius") {
            params.center = {
              type: "Point",
              coordinates: [geoPeri.longitude, geoPeri.latitude],
            };
            params.radius = geoPeri.radius;
          }
        }
      }

      // En mode preview on peut avoir un widgetId vide, on utilise 0 pour quand même requêter si on a choisi une sélection
      if (!widgetId && modePreview) {
        widgetId = "0";
        if (!config.projetId || !config.selections.length) {
          throw new Error(
            "ProjectId and Selections are required when no widgetId is provided",
          );
        }
      }

      // On fait un clean des filtres pour n'envoyer que les valeurs
      filtersToSend = this.cleanFilters(filtersToSend);

      // Encoder manuellement le paramètre query pour éviter les erreurs RFC 7230/3986
      const queryString = encodeURIComponent(JSON.stringify(params));
      const isAgenda =
        config.templateType === WidgetTemplateType.AGENDA ||
        config.templateType === WidgetTemplateType.AGENDA_MAP;
      let url = isAgenda
        ? `/api/v002/public/widgets/${widgetId}/agenda?query=${queryString}`
        : `/api/v002/public/widgets/${widgetId}/objets?query=${queryString}`;
      if (filtersToSend && filtersToSend.size > 0) {
        // Convertir la Map en objet pour la sérialisation JSON
        const filtersObject: Record<string, FacetValue[]> = {};
        filtersToSend.forEach((values, key) => {
          filtersObject[key] = values;
        });
        url += `&filters=${encodeURIComponent(JSON.stringify(filtersObject))}`;
      }
      if (facetsConfig && Object.keys(facetsConfig).length > 0) {
        url += `&facetsConfig=${encodeURIComponent(JSON.stringify(facetsConfig))}`;
      }

      if (isAgenda) {
        const response = await this.apiService.get<ApidaeAgendaResponse>(url);
        const objetsByDate = response.objetsTouristiques || {};
        const items = Object.values(objetsByDate).flat();
        return {
          items,
          facets: response.facets || new Map(),
          total: response.numFound,
          hasMore: effectiveFirst + items.length < response.numFound,
          groupedByDate: objetsByDate,
          numFoundParents: response.numFoundParents,
        };
      }

      const response = await this.apiService.get<ApidaeSearchResponse>(url);

      return {
        items: response.objetsTouristiques || [],
        facets: response.facets || new Map(),
        total: response.numFound,
        hasMore: response.numFound > page * pageSize,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
      throw new Error("Impossible de charger les données touristiques");
    }
  }

  cleanFilters(
    filtersToSend: Map<FacetId, FacetValue[]>,
  ): Map<FacetId, FacetValue[]> {
    let cleanedFilters = new Map<FacetId, FacetValue[]>();
    filtersToSend.forEach((values, key) => {
      cleanedFilters.set(
        key,
        values.map((v) => ({ val: v.val })),
      );
    });
    return cleanedFilters;
  }
}
